// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Select,
  SimpleGrid,
  Progress,
  Loader,
  Center,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconClipboardList,
  IconCheck,
  IconAlertTriangle,
  IconUser,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useOnboardingChecklists,
  useCreateOnboardingChecklist,
} from '../../hooks/useRecruitment';
import type {
  OnboardingChecklist,
  CreateOnboardingChecklistInput,
} from '../../types/recruitment';

export default function OnboardingListPage() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    employeeId: '',
    templateId: '',
    startDate: null as string | null,
    targetCompletionDate: null as string | null,
  });

  const { data: checklistsData, loading: isLoading } = useOnboardingChecklists();
  const checklists = (checklistsData as any)?.onboardingChecklists as OnboardingChecklist[] | undefined;
  const createMutation = useCreateOnboardingChecklist();

  // ── Stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!checklists) return { active: 0, completedThisMonth: 0, overdueItems: 0 };
    const active = checklists.filter((c) => c.status === 'IN_PROGRESS' || c.status === 'NOT_STARTED').length;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const completedThisMonth = checklists.filter(
      (c) => c.status === 'COMPLETED' && new Date(c.targetCompletionDate) >= monthStart,
    ).length;
    let overdueItems = 0;
    for (const checklist of checklists) {
      if (checklist.status === 'COMPLETED') continue;
      for (const item of checklist.items) {
        if (
          item.dueDate &&
          item.status !== 'COMPLETED' &&
          item.status !== 'SKIPPED' &&
          new Date(item.dueDate) < now
        ) {
          overdueItems++;
        }
      }
    }
    return { active, completedThisMonth, overdueItems };
  }, [checklists]);

  const handleCreate = async () => {
    if (!form.employeeId || !form.startDate || !form.targetCompletionDate) return;
    const input: CreateOnboardingChecklistInput = {
      employeeId: form.employeeId,
      templateId: form.templateId || undefined,
      startDate: form.startDate,
      targetCompletionDate: form.targetCompletionDate,
    };

    try {
      await createMutation.mutateAsync(input);
      notifications.show({ title: 'Created', message: 'Onboarding checklist created', color: 'green' });
      setModalOpen(false);
      setForm({ employeeId: '', templateId: '', startDate: null, targetCompletionDate: null });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create checklist',
        color: 'red',
      });
    }
  };

  // ── Table Columns ─────────────────────────────────────────────────
  const columns: Column<OnboardingChecklist>[] = [
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      width: 120,
      render: (row) => new Date(row.startDate).toLocaleDateString(),
    },
    {
      key: 'targetCompletionDate',
      label: 'Target Completion',
      sortable: true,
      width: 140,
      render: (row) => new Date(row.targetCompletionDate).toLocaleDateString(),
    },
    {
      key: 'completedCount',
      label: 'Progress',
      width: 180,
      render: (row) => {
        const pct = row.totalCount > 0 ? Math.round((row.completedCount / row.totalCount) * 100) : 0;
        return (
          <Stack gap={4}>
            <Progress value={pct} size="sm" color={pct === 100 ? 'green' : 'blue'} />
            <Text size="xs" c="dimmed" ta="center">
              {pct}%
            </Text>
          </Stack>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'totalCount',
      label: 'Items',
      width: 100,
      render: (row) => (
        <Text size="sm">
          {row.completedCount}/{row.totalCount}
        </Text>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Onboarding"
        subtitle={checklists ? `${checklists.length} checklists` : undefined}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={() => setModalOpen(true)}>
            New Checklist
          </Button>
        }
      />

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard
          title="Active Onboardings"
          value={stats.active}
          icon={<IconUser size={24} />}
          color="blue"
        />
        <StatCard
          title="Completed This Month"
          value={stats.completedThisMonth}
          icon={<IconCheck size={24} />}
          color="green"
        />
        <StatCard
          title="Overdue Items"
          value={stats.overdueItems}
          icon={<IconAlertTriangle size={24} />}
          color="red"
        />
      </SimpleGrid>

      {/* Table */}
      <DataTable<Record<string, unknown>>
        columns={columns as unknown as import('../../components/DataTable').Column<Record<string, unknown>>[]}
        data={(checklists ?? []) as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => navigate(`/admin/workforce/onboarding/${row.id}`)}
        getRowKey={(row) => row.id as string}
        emptyMessage="No onboarding checklists found"
      />

      {/* Create Checklist Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New Onboarding Checklist"
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Employee ID"
            required
            value={form.employeeId}
            onChange={(e) => setForm((f) => ({ ...f, employeeId: e.currentTarget.value }))}
            placeholder="Employee ID"
          />

          <TextInput
            label="Template ID"
            value={form.templateId}
            onChange={(e) => setForm((f) => ({ ...f, templateId: e.currentTarget.value }))}
            placeholder="Optional - leave blank for empty checklist"
          />

          <DateInput
            label="Start Date"
            required
            value={form.startDate}
            onChange={(v) => setForm((f) => ({ ...f, startDate: v }))}
            placeholder="Select start date"
          />

          <DateInput
            label="Target Completion Date"
            required
            value={form.targetCompletionDate}
            onChange={(v) => setForm((f) => ({ ...f, targetCompletionDate: v }))}
            placeholder="Select target date"
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              loading={createMutation.isPending}
              disabled={!form.employeeId || !form.startDate || !form.targetCompletionDate}
            >
              Create Checklist
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
